import React, { useEffect, useState, useRef } from 'react';
import {
  Save, Folder, FolderOpen, ChevronDown, ChevronRight, Check, Plus,
  Trash2, Upload, FileText, GripVertical, Pencil, X, FolderPlus, Shield
} from 'lucide-react';
import { useApi } from './DashboardLayout';
import TipTapEditor from '../components/TipTapEditor';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function fileColor(name) {
  const ext = name?.split('.').pop()?.toLowerCase();
  const colors = { pdf: '#EF4444', doc: '#3B82F6', docx: '#3B82F6', xls: '#10B981', xlsx: '#10B981', ppt: '#F59E0B', pptx: '#F59E0B', zip: '#8B5CF6', jpg: '#EC4899', png: '#EC4899', txt: '#6B7280' };
  return colors[ext] || '#6B7280';
}

/* ─── Sortable file row inside a category ─── */
function SortableAdminFile({ f, onDelete, editingFile, onStartEdit, editForm, setEditForm, onSaveEdit, onCancelEdit }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: f.path });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const isEditing = editingFile === f.path;
  const displayName = f.display_name || f.name;
  return (
    <div ref={setNodeRef} style={{ ...style, borderBottom: '1px solid var(--border)' }}
      data-testid={`settings-file-${f.name}`}>
      {/* Main row */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', gap: 10, fontSize: 13 }}>
        <div {...attributes} {...listeners} style={{
          cursor: 'grab', color: 'var(--text-muted)', opacity: 0.4, display: 'flex', alignItems: 'center'
        }}>
          <GripVertical size={14} />
        </div>
        <div style={{
          width: 28, height: 28, borderRadius: 6, display: 'flex', alignItems: 'center',
          justifyContent: 'center', background: fileColor(f.name) + '15', flexShrink: 0,
        }}>
          <FileText size={15} style={{ color: fileColor(f.name) }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {displayName}
          </div>
          {f.description && !isEditing && (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.4, marginTop: 1 }}>
              {f.description}
            </div>
          )}
          {f.display_name && f.display_name !== f.name && (
            <div style={{ fontSize: 10, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Datei: {f.name}
            </div>
          )}
        </div>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>{formatSize(f.size)}</span>
        <button className="btn-icon" onClick={() => onStartEdit(f)}
          title="Anzeigename & Beschreibung bearbeiten" style={{ flexShrink: 0 }}
          data-testid={`edit-file-meta-${f.name}`}>
          <Pencil size={13} />
        </button>
        <button className="btn-icon" onClick={() => onDelete(f)} title="Löschen" style={{ color: '#EF4444', flexShrink: 0 }}
          data-testid={`delete-settings-file-${f.name}`}>
          <Trash2 size={14} />
        </button>
      </div>

      {/* Edit panel */}
      {isEditing && (
        <div style={{
          padding: '10px 12px 12px 54px', background: 'var(--bg-secondary, #f9fafb)',
          borderTop: '1px solid var(--border)',
        }} data-testid={`edit-panel-${f.name}`}>
          <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 3, color: 'var(--text-muted)' }}>Anzeigename</label>
              <input className="of-input" value={editForm.display_name}
                onChange={e => setEditForm(p => ({ ...p, display_name: e.target.value }))}
                placeholder={f.name} style={{ fontSize: 12, padding: '5px 8px' }}
                data-testid="edit-display-name-input" />
            </div>
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 3, color: 'var(--text-muted)' }}>Beschreibung</label>
            <textarea className="of-input" rows={2} value={editForm.description}
              onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))}
              placeholder="Kurze Beschreibung zum Dokument..."
              style={{ fontSize: 12, padding: '5px 8px', resize: 'vertical' }}
              data-testid="edit-description-input" />
          </div>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary btn-sm" onClick={onCancelEdit} style={{ fontSize: 11, padding: '4px 10px' }}>Abbrechen</button>
            <button className="btn btn-primary btn-sm" onClick={() => onSaveEdit(f)}
              style={{ fontSize: 11, padding: '4px 10px' }} data-testid="save-file-meta-btn">
              <Save size={12} /> Speichern
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Sortable category row (for reordering) ─── */
function SortableCategoryHandle({ id }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  return (
    <div ref={setNodeRef} {...attributes} {...listeners}
      style={{
        cursor: 'grab', color: 'var(--text-muted)', opacity: 0.4, display: 'flex',
        alignItems: 'center', padding: '0 4px', flexShrink: 0,
        transform: CSS.Transform.toString(transform), transition,
      }}
      data-testid={`drag-cat-handle`}
    >
      <GripVertical size={16} />
    </div>
  );
}

export default function DownloadSettingsPage() {
  const { apiFetch } = useApi();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Intro text
  const [introContent, setIntroContent] = useState('');
  const [introLoaded, setIntroLoaded] = useState(false);
  const [introSaving, setIntroSaving] = useState(false);
  const [introMsg, setIntroMsg] = useState('');

  // Categories + files
  const [categories, setCategories] = useState([]); // [{name, path, description, files:[]}]
  const [catMeta, setCatMeta] = useState({}); // path → description
  const [expandedCat, setExpandedCat] = useState(null);
  const [catEdits, setCatEdits] = useState({}); // path → edited description
  const [catSaving, setCatSaving] = useState(null);
  const [catMsg, setCatMsg] = useState({});
  const [loadingCats, setLoadingCats] = useState(true);

  // New category
  const [showNewCat, setShowNewCat] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [creatingCat, setCreatingCat] = useState(false);

  // Rename
  const [renamingCat, setRenamingCat] = useState(null); // path
  const [renameVal, setRenameVal] = useState('');

  // File uploads
  const fileInputRefs = useRef({});
  const [uploadingCat, setUploadingCat] = useState(null);

  // File rename (display name)
  const [renamingFile, setRenamingFile] = useState(null); // file path
  const [renameFileVal, setRenameFileVal] = useState('');

  // File edit (display name + description)
  const [editingFile, setEditingFile] = useState(null); // file path being edited
  const [editForm, setEditForm] = useState({ display_name: '', description: '' });

  // Category visibility
  const [visibilityMap, setVisibilityMap] = useState({}); // path → 'all' | 'admins' | 'gbm'

  useEffect(() => {
    loadIntro();
    loadAllCategories();
  }, []);

  // ── Load intro ──
  const loadIntro = async () => {
    const r = await apiFetch('/api/downloads/intro');
    const d = await r?.json();
    if (d) setIntroContent(d.content || '');
    setIntroLoaded(true);
  };

  // ── Load categories with files ──
  const loadAllCategories = async () => {
    setLoadingCats(true);
    try {
      const r = await apiFetch('/api/downloads/categories-with-files');
      if (r?.ok) {
        const d = await r.json();
        setCategories(d.categories || []);
        // Build meta map + visibility map
        const meta = {};
        const vis = {};
        (d.categories || []).forEach(c => {
          if (c.description) meta[c.path] = c.description;
          vis[c.path] = c.visibility || 'all';
        });
        setCatMeta(meta);
        setVisibilityMap(vis);
        setCatEdits(prev => {
          const updated = { ...prev };
          (d.categories || []).forEach(c => {
            if (updated[c.path] === undefined && c.description) updated[c.path] = c.description;
          });
          return updated;
        });
      }
    } catch { /* ignore */ }
    setLoadingCats(false);
  };

  const saveVisibility = async (catPath, visibility) => {
    const desc = catEdits[catPath] || catMeta[catPath] || '';
    await apiFetch('/api/downloads/category-meta', {
      method: 'PUT',
      body: JSON.stringify({ path: catPath, description: desc, visibility })
    });
    setVisibilityMap(prev => ({ ...prev, [catPath]: visibility }));
  };

  // ── Save intro ──
  const saveIntro = async () => {
    setIntroSaving(true); setIntroMsg('');
    const r = await apiFetch('/api/downloads/intro', {
      method: 'PUT', body: JSON.stringify({ content: introContent })
    });
    if (r?.ok) { setIntroMsg('Gespeichert'); setTimeout(() => setIntroMsg(''), 3000); }
    setIntroSaving(false);
  };

  // ── Save category description ──
  const saveCatDescription = async (path) => {
    setCatSaving(path);
    setCatMsg(prev => ({ ...prev, [path]: '' }));
    const r = await apiFetch('/api/downloads/category-meta', {
      method: 'PUT', body: JSON.stringify({ path, description: catEdits[path] || '' })
    });
    if (r?.ok) {
      setCatMeta(prev => ({ ...prev, [path]: catEdits[path] || '' }));
      setCatMsg(prev => ({ ...prev, [path]: 'Gespeichert' }));
      setTimeout(() => setCatMsg(prev => ({ ...prev, [path]: '' })), 3000);
      // Update in categories array too
      setCategories(prev => prev.map(c => c.path === path ? { ...c, description: catEdits[path] || '' } : c));
    }
    setCatSaving(null);
  };

  // ── Create category ──
  const createCategory = async () => {
    if (!newCatName.trim()) return;
    setCreatingCat(true);
    const r = await apiFetch('/api/downloads/categories', {
      method: 'POST', body: JSON.stringify({ name: newCatName.trim() })
    });
    if (r?.ok) {
      setNewCatName(''); setShowNewCat(false);
      await loadAllCategories();
    } else {
      const err = await r?.json().catch(() => null);
      alert(err?.detail || 'Fehler beim Erstellen');
    }
    setCreatingCat(false);
  };

  // ── Rename category ──
  const doRenameCategory = async (cat) => {
    if (!renameVal.trim()) return;
    const r = await apiFetch(`/api/downloads/categories/${encodeURIComponent(cat.path)}`, {
      method: 'PUT', body: JSON.stringify({ name: renameVal.trim(), old_path: cat.path })
    });
    if (r?.ok) {
      setRenamingCat(null); setRenameVal('');
      await loadAllCategories();
    } else {
      const err = await r?.json().catch(() => null);
      alert(err?.detail || 'Fehler beim Umbenennen');
    }
  };

  // ── Delete category ──
  const deleteCategory = async (cat) => {
    if (!window.confirm(`"${cat.name}" in den Papierkorb verschieben?`)) return;
    const r = await apiFetch('/api/downloads/categories', {
      method: 'DELETE', body: JSON.stringify({ path: cat.path })
    });
    if (r?.ok) await loadAllCategories();
    else { const err = await r?.json().catch(() => null); alert(err?.detail || 'Fehler beim Löschen'); }
  };

  // ── Upload file to category ──
  const uploadFileToCat = async (catPath, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCat(catPath);
    const formData = new FormData();
    formData.append('file', file);
    await apiFetch(`/api/downloads/upload?path=${encodeURIComponent(catPath)}`, { method: 'POST', body: formData });
    setUploadingCat(null);
    if (fileInputRefs.current[catPath]) fileInputRefs.current[catPath].value = '';
    await loadAllCategories();
  };

  // ── Delete file ──
  const deleteFile = async (file) => {
    if (!window.confirm(`"${file.display_name || file.name}" in den Papierkorb verschieben?`)) return;
    await apiFetch('/api/downloads/file', { method: 'DELETE', body: JSON.stringify({ path: file.path }) });
    await loadAllCategories();
  };

  // ── File display name rename ──
  const startRenameFile = (file) => {
    setRenamingFile(file.path);
    setRenameFileVal(file.display_name || file.name);
  };

  const doRenameFile = async (file) => {
    if (!renameFileVal.trim()) return;
    const r = await apiFetch('/api/downloads/file-meta', {
      method: 'PUT', body: JSON.stringify({ path: file.path, display_name: renameFileVal.trim() })
    });
    if (r?.ok) {
      // Update locally
      setCategories(prev => prev.map(c => ({
        ...c,
        files: c.files.map(f => f.path === file.path
          ? { ...f, display_name: renameFileVal.trim() }
          : f
        ),
      })));
    }
    setRenamingFile(null); setRenameFileVal('');
  };

  const cancelRenameFile = () => { setRenamingFile(null); setRenameFileVal(''); };

  // ── File edit (display name + description) ──
  const startEditFile = (file) => {
    setEditingFile(file.path);
    setEditForm({
      display_name: file.display_name || '',
      description: file.description || ''
    });
  };

  const saveEditFile = async (file) => {
    const r = await apiFetch('/api/downloads/file-meta', {
      method: 'PUT',
      body: JSON.stringify({
        path: file.path,
        display_name: editForm.display_name.trim(),
        description: editForm.description.trim()
      })
    });
    if (r?.ok) {
      // Update locally
      setCategories(prev => prev.map(c => ({
        ...c,
        files: c.files.map(f => f.path === file.path
          ? { ...f, display_name: editForm.display_name.trim() || f.display_name, description: editForm.description.trim() }
          : f
        ),
      })));
    }
    setEditingFile(null);
    setEditForm({ display_name: '', description: '' });
  };

  const cancelEditFile = () => {
    setEditingFile(null);
    setEditForm({ display_name: '', description: '' });
  };

  // ── Category sort via DnD ──
  const handleCatDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = categories.findIndex(c => c.path === active.id);
    const newIndex = categories.findIndex(c => c.path === over.id);
    const newOrder = arrayMove(categories, oldIndex, newIndex);
    setCategories(newOrder);
    await apiFetch('/api/downloads/sort-order/categories', {
      method: 'PUT', body: JSON.stringify({ order: newOrder.map(c => c.path) })
    });
  };

  // ── File sort within a category ──
  const handleFileDragEnd = async (catPath, event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const cat = categories.find(c => c.path === catPath);
    if (!cat) return;
    const oldIndex = cat.files.findIndex(f => f.path === active.id);
    const newIndex = cat.files.findIndex(f => f.path === over.id);
    const newFiles = arrayMove(cat.files, oldIndex, newIndex);
    setCategories(prev => prev.map(c => c.path === catPath ? { ...c, files: newFiles } : c));
    await apiFetch('/api/downloads/sort-order/files', {
      method: 'PUT', body: JSON.stringify({ category_path: catPath, order: newFiles.map(f => f.path) })
    });
  };

  const toggleCat = (path) => {
    if (expandedCat === path) {
      setExpandedCat(null);
    } else {
      setExpandedCat(path);
      if (catEdits[path] === undefined) {
        setCatEdits(prev => ({ ...prev, [path]: catMeta[path] || '' }));
      }
    }
  };

  return (
    <div data-testid="download-settings-page">
      <style>{`
        .ds-cat-card {
          border: 1px solid var(--border); border-radius: 10px; overflow: hidden;
          margin-bottom: 8px; background: var(--bg-primary, #fff);
          transition: box-shadow 0.15s;
        }
        .ds-cat-card:hover { box-shadow: 0 1px 4px rgba(0,0,0,0.04); }
        .ds-cat-header {
          display: flex; align-items: center; padding: 12px 16px; cursor: pointer;
          gap: 8px; transition: background 0.15s; user-select: none;
        }
        .ds-cat-header:hover { background: var(--bg-secondary, #f9fafb); }
        .ds-cat-body { border-top: 1px solid var(--border); }
        .ds-section-label {
          font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
          color: var(--text-muted); margin-bottom: 8px;
        }
      `}</style>

      <div className="page-header" style={{ marginBottom: 20 }}>
        <h2>Download-Bereich verwalten</h2>
        <p>Kategorien, Beschreibungen und Dateien konfigurieren</p>
      </div>

      {/* ═══ Intro text ═══ */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-body">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div>
              <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Einleitungstext</h4>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
                Wird oben auf der Download-Seite angezeigt
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {introMsg && (
                <span style={{ fontSize: 12, color: '#10B981', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Check size={14} /> {introMsg}
                </span>
              )}
              <button className="btn btn-primary btn-sm" onClick={saveIntro} disabled={introSaving}
                data-testid="save-intro-btn" style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                <Save size={14} /> {introSaving ? 'Speichern...' : 'Speichern'}
              </button>
            </div>
          </div>
          {introLoaded && (
            <TipTapEditor content={introContent} onChange={setIntroContent}
              placeholder="Einleitungstext für den Download-Bereich eingeben..." />
          )}
        </div>
      </div>

      {/* ═══ Categories management ═══ */}
      <div className="card">
        <div className="card-body">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div>
              <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Kategorien</h4>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
                Kategorien mit Beschreibungen und Dateien verwalten. Reihenfolge per Drag & Drop ändern.
              </p>
            </div>
            <button className="btn btn-primary btn-sm" onClick={() => setShowNewCat(true)}
              data-testid="add-category-btn"
              style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
              <FolderPlus size={14} /> Neue Kategorie
            </button>
          </div>

          {/* New category form */}
          {showNewCat && (
            <div style={{
              display: 'flex', alignItems: 'flex-end', gap: 10, marginBottom: 16,
              padding: '12px 14px', background: 'var(--bg-secondary, #f9fafb)', borderRadius: 8,
              border: '1px solid var(--border)',
            }}>
              <div style={{ flex: 1, maxWidth: 320 }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Kategoriename</label>
                <input className="of-input" value={newCatName} onChange={e => setNewCatName(e.target.value)}
                  placeholder="z.B. Verträge und Vergütungsvereinbarungen" autoFocus
                  onKeyDown={e => e.key === 'Enter' && createCategory()}
                  data-testid="new-cat-name-input" />
              </div>
              <button className="btn btn-primary btn-sm" onClick={createCategory}
                disabled={!newCatName.trim() || creatingCat}
                data-testid="create-cat-btn"
                style={{ padding: '7px 16px' }}>
                {creatingCat ? 'Erstellen...' : 'Erstellen'}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => { setShowNewCat(false); setNewCatName(''); }}
                style={{ padding: '7px 12px' }}>Abbrechen</button>
            </div>
          )}

          {/* Categories list */}
          {loadingCats ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Kategorien werden geladen...</div>
          ) : categories.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              <Folder size={32} style={{ opacity: 0.2, marginBottom: 8 }} />
              <p style={{ margin: 0 }}>Keine Kategorien vorhanden. Erstellen Sie die erste Kategorie.</p>
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCatDragEnd}>
              <SortableContext items={categories.map(c => c.path)} strategy={verticalListSortingStrategy}>
                {categories.map(cat => {
                  const isOpen = expandedCat === cat.path;
                  const isRenaming = renamingCat === cat.path;

                  return (
                    <div key={cat.path} className="ds-cat-card" data-testid={`settings-cat-${cat.name}`}>
                      {/* Category header */}
                      <div className="ds-cat-header">
                        <SortableCategoryHandle id={cat.path} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0, cursor: 'pointer' }}
                          onClick={() => toggleCat(cat.path)}>
                          {isOpen ? <ChevronDown size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} /> :
                            <ChevronRight size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />}
                          <FolderOpen size={16} style={{ color: 'var(--primary)', opacity: 0.8, flexShrink: 0 }} />

                          {isRenaming ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }} onClick={e => e.stopPropagation()}>
                              <input className="of-input" value={renameVal} onChange={e => setRenameVal(e.target.value)}
                                autoFocus style={{ fontSize: 13, padding: '4px 8px', maxWidth: 260 }}
                                onKeyDown={e => { if (e.key === 'Enter') doRenameCategory(cat); if (e.key === 'Escape') setRenamingCat(null); }}
                                data-testid="rename-cat-input" />
                              <button className="btn btn-primary btn-sm" onClick={() => doRenameCategory(cat)}
                                style={{ padding: '3px 10px', fontSize: 11 }}>OK</button>
                              <button className="btn btn-secondary btn-sm" onClick={() => setRenamingCat(null)}
                                style={{ padding: '3px 10px', fontSize: 11 }}>
                                <X size={12} />
                              </button>
                            </div>
                          ) : (
                            <span style={{ fontSize: 14, fontWeight: 600, wordBreak: 'break-word' }}>{cat.name}</span>
                          )}
                        </div>

                        {/* File count badge */}
                        <span style={{
                          fontSize: 11, color: 'var(--text-muted)', background: 'var(--bg-secondary, #f0f0f0)',
                          padding: '2px 8px', borderRadius: 10, fontWeight: 600, flexShrink: 0,
                        }}>
                          {cat.files.length} {cat.files.length === 1 ? 'Datei' : 'Dateien'}
                        </span>

                        {/* Action buttons */}
                        {!isRenaming && (
                          <div style={{ display: 'flex', gap: 2, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                            <button className="btn-icon" title="Umbenennen"
                              onClick={() => { setRenamingCat(cat.path); setRenameVal(cat.name); }}
                              data-testid={`rename-cat-${cat.name}`}>
                              <Pencil size={13} />
                            </button>
                            <button className="btn-icon" title="Kategorie löschen" style={{ color: '#EF4444' }}
                              onClick={() => deleteCategory(cat)}
                              data-testid={`delete-cat-${cat.name}`}>
                              <Trash2 size={13} />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Expanded body */}
                      {isOpen && (
                        <div className="ds-cat-body">
                          {/* Description editor */}
                          <div style={{ padding: '16px' }}>
                            <div className="ds-section-label">Beschreibung</div>
                            <TipTapEditor
                              content={catEdits[cat.path] || ''}
                              onChange={val => setCatEdits(prev => ({ ...prev, [cat.path]: val }))}
                              placeholder={`Beschreibung für "${cat.name}" eingeben...`}
                            />
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
                              {catMsg[cat.path] && (
                                <span style={{ fontSize: 12, color: '#10B981', display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <Check size={14} /> {catMsg[cat.path]}
                                </span>
                              )}
                              <button className="btn btn-primary btn-sm"
                                onClick={() => saveCatDescription(cat.path)}
                                disabled={catSaving === cat.path}
                                data-testid={`save-cat-desc-${cat.name}`}
                                style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                                <Save size={14} /> {catSaving === cat.path ? 'Speichern...' : 'Beschreibung speichern'}
                              </button>
                            </div>
                          </div>

                          {/* Visibility switch */}
                          <div style={{
                            borderTop: '1px solid var(--border)', padding: '12px 16px',
                          }} data-testid={`visibility-setting-${cat.name}`}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                              <Shield size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Sichtbarkeit</span>
                            </div>
                            <div style={{
                              display: 'inline-flex', borderRadius: 8, overflow: 'hidden',
                              border: '1px solid var(--border)', background: 'var(--bg-secondary)',
                            }} data-testid={`visibility-select-${cat.name}`}>
                              {[
                                { value: 'all', label: 'Alle', color: '#16A34A', bg: '#F0FDF4' },
                                { value: 'admins', label: 'Nur Admins', color: '#EA580C', bg: '#FFF7ED' },
                                { value: 'gbm', label: 'GBM', color: '#2563EB', bg: '#EFF6FF' },
                              ].map(opt => {
                                const active = (visibilityMap[cat.path] || 'all') === opt.value;
                                return (
                                  <button key={opt.value}
                                    onClick={() => { if (!active) saveVisibility(cat.path, opt.value); }}
                                    data-testid={`visibility-btn-${opt.value}-${cat.name}`}
                                    style={{
                                      padding: '5px 12px', fontSize: 12, fontWeight: active ? 700 : 500,
                                      border: 'none', cursor: active ? 'default' : 'pointer',
                                      background: active ? opt.bg : 'transparent',
                                      color: active ? opt.color : 'var(--text-muted)',
                                      transition: 'all 0.15s',
                                      borderRight: '1px solid var(--border)',
                                    }}
                                  >{opt.label}</button>
                                );
                              })}
                            </div>
                          </div>

                          {/* Files section */}
                          <div style={{ borderTop: '1px solid var(--border)', padding: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                              <div className="ds-section-label" style={{ marginBottom: 0 }}>
                                Dateien ({cat.files.length})
                              </div>
                              <div>
                                <input type="file"
                                  ref={el => { fileInputRefs.current[cat.path] = el; }}
                                  onChange={e => uploadFileToCat(cat.path, e)}
                                  style={{ display: 'none' }}
                                  data-testid={`upload-input-${cat.name}`} />
                                <button className="btn btn-primary btn-sm"
                                  onClick={() => fileInputRefs.current[cat.path]?.click()}
                                  disabled={uploadingCat === cat.path}
                                  data-testid={`upload-btn-${cat.name}`}
                                  style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                                  <Upload size={13} /> {uploadingCat === cat.path ? 'Hochladen...' : 'Datei hochladen'}
                                </button>
                              </div>
                            </div>

                            {cat.files.length === 0 ? (
                              <div style={{
                                padding: '16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 12,
                                background: 'var(--bg-secondary, #f9fafb)', borderRadius: 6,
                                border: '1px dashed var(--border)',
                              }}>
                                Keine Dateien. Laden Sie Dateien in diese Kategorie hoch.
                              </div>
                            ) : (
                              <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                                <DndContext sensors={sensors} collisionDetection={closestCenter}
                                  onDragEnd={(e) => handleFileDragEnd(cat.path, e)}>
                                  <SortableContext items={cat.files.map(f => f.path)} strategy={verticalListSortingStrategy}>
                                    {cat.files.map(f => (
                                      <SortableAdminFile key={f.path} f={f}
                                        onDelete={deleteFile}
                                        editingFile={editingFile}
                                        onStartEdit={startEditFile}
                                        editForm={editForm}
                                        setEditForm={setEditForm}
                                        onSaveEdit={saveEditFile}
                                        onCancelEdit={cancelEditFile} />
                                    ))}
                                  </SortableContext>
                                </DndContext>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>
    </div>
  );
}
